
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FormulaMaterialStep } from './wizard/FormulaMaterialStep';
import { FormulaDetailsStep } from './wizard/FormulaDetailsStep';
import { FormulaCompositionStep } from './wizard/FormulaCompositionStep';
import { FormulaReviewStep } from './wizard/FormulaReviewStep';
import { useCreateFormula } from '@/hooks/useCreateFormula';
import { Toast } from '@/components/ui/toast';
import { useToast } from '@/hooks/use-toast';

interface Material {
  id: string;
  name: string;
  type: string;
  cas_number?: string | null;
}

interface FormulaWizardProps {
  portfolioId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const FormulaWizard = ({ portfolioId, isOpen, onClose }: FormulaWizardProps) => {
  const [step, setStep] = useState<string>('details');
  const { toast } = useToast();
  const [formulaData, setFormulaData] = useState({
    name: '',
    description: '',
    materials: [] as Material[],
    chemicals: [] as any[],
  });
  
  const { createFormula, isLoading } = useCreateFormula(portfolioId);
  
  const handleNext = () => {
    if (step === 'details') setStep('materials');
    else if (step === 'materials') setStep('composition');
    else if (step === 'composition') setStep('review');
  };
  
  const handlePrevious = () => {
    if (step === 'materials') setStep('details');
    else if (step === 'composition') setStep('materials');
    else if (step === 'review') setStep('composition');
  };
  
  const handleSubmit = async () => {
    try {
      await createFormula.mutateAsync(formulaData);
      toast({
        title: "Formula Created",
        description: `${formulaData.name} has been successfully created.`,
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create formula. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const updateFormulaData = (data: Partial<typeof formulaData>) => {
    setFormulaData(prev => ({ ...prev, ...data }));
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Formula</DialogTitle>
          <DialogDescription>
            Define the chemical composition of your product formula through this step-by-step process.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={step} className="mt-4">
          <TabsList className="grid grid-cols-4 mb-8">
            <TabsTrigger value="details" onClick={() => setStep('details')}>
              Details
            </TabsTrigger>
            <TabsTrigger value="materials" onClick={() => setStep('materials')}>
              Materials
            </TabsTrigger>
            <TabsTrigger value="composition" onClick={() => setStep('composition')}>
              Composition
            </TabsTrigger>
            <TabsTrigger value="review" onClick={() => setStep('review')}>
              Review
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="details">
            <FormulaDetailsStep 
              formData={formulaData}
              updateFormData={updateFormulaData}
            />
          </TabsContent>
          
          <TabsContent value="materials">
            <FormulaMaterialStep 
              formData={formulaData}
              updateFormData={updateFormulaData}
            />
          </TabsContent>
          
          <TabsContent value="composition">
            <FormulaCompositionStep 
              formData={formulaData}
              updateFormData={updateFormulaData}
            />
          </TabsContent>
          
          <TabsContent value="review">
            <FormulaReviewStep formData={formulaData} />
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="flex justify-between mt-6">
          {step !== 'details' && (
            <Button variant="outline" onClick={handlePrevious}>
              Previous
            </Button>
          )}
          <div>
            {step !== 'review' ? (
              <Button onClick={handleNext}>Next</Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create Formula'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
